import React, { useState } from 'react';
import { toast } from 'sonner';
import instance from '../redux/actions/AxiosInstance/authenticated';

const SpecialPlans = ({ onStartTrial }) => {
  const [couponCode, setCouponCode] = useState('');
  const [planData, setPlanData] = useState(null);
  const [couponInfo, setCouponInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await instance.post(
        process.env.REACT_APP_BASE_URL + '/chargebee/special-plans',
        { coupon: couponCode.trim() }
      );

      setPlanData(response.data.data);
      setCouponInfo(response.data.coupon);
      setError('');
      toast.success('Coupon validated successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to validate coupon';
      setError(errorMessage);
      setPlanData(null);
      setCouponInfo(null);
      
      switch (err.response?.data?.error) {
        case 'COUPON_NOT_FOUND':
          toast.error('Invalid coupon code');
          break;
        case 'COUPON_USED':
          toast.error('This coupon has already been used');
          break;
        case 'COUPON_EXPIRED':
          toast.error('This coupon has expired');
          break;
        case 'NO_REDEMPTIONS':
          toast.error('This coupon has no remaining uses');
          break;
        default:
          toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="w-[40vw] mx-auto p-6">
      <div className="mb-4">
        <p className="text-white">
          Have a coupon code? Enter it below to unlock exclusive plans and offers!
        </p>
      </div>

      <div className="bg-[#222222] rounded-xl shadow-lg p-6 mb-8 border border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="coupon" className="block text-sm font-medium text-white mb-2">
              Coupon Code
            </label>
            <input
              id="coupon"
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && validateCoupon()}
              placeholder="Enter your coupon code"
              className="w-full px-4 py-3 bg-[#333333] border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200 font-mono text-lg tracking-wider text-white placeholder-gray-400"
              disabled={loading}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={validateCoupon}
              disabled={loading}
              className="bg-primary text-primary-dark px-8 py-3 rounded-lg hover:opacity-90 transition duration-300 font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Validating...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Apply Coupon
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-900/20 border border-red-600 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <span className="text-red-400 font-medium">{error}</span>
            </div>
          </div>
        )}
      </div>

      {planData && couponInfo && (
        <div className="bg-primary-bg rounded-xl shadow-lg overflow-hidden border border-border/100">
          <div className="bg-primary text-primary-dark p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span className="font-semibold">Valid Coupon: {couponInfo.code}</span>
              </div>
              <div className="text-sm bg-primary-dark bg-opacity-30 px-3 py-1 rounded-full">
                Special offer
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm opacity-90">
              <span>Valid until: {formatDate(couponInfo.valid_till)}</span>
              <span>•</span>
              <span>One Time use only</span>
            </div>
          </div>

          <div className="p-8">
            <div className="bg-[#222222] rounded-xl shadow-md p-6 max-w-md mx-auto">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-4">
                  {planData.name}
                </h3>
                
                <div className="text-4xl font-bold text-primary mb-2 flex items-center justify-center">
                  <span>${planData.renewal_price}</span>
                  <div className="text-sm flex flex-col leading-tight text-left text-white ml-2">
                    <span className="font-normal">per</span>
                    <span className="font-normal">
                      {planData.billing_period === 30 ? 'month' : 
                       planData.billing_period === 365 ? 'year' : 'period'}
                    </span>
                  </div>
                </div>

                {planData.active ? (
                  <div className="bg-primary/20 text-primary py-2 px-4 rounded-lg font-medium">
                    ✓ Currently Active
                  </div>
                ) : (
                  <button
                    onClick={() => onStartTrial && onStartTrial({ ...planData, coupon: couponInfo.code })}
                    className="w-full bg-primary text-primary-dark font-bold py-3 px-6 rounded-lg hover:opacity-90 transition duration-300 shadow-md"
                  >
                    Start Trial
                  </button>
                )}

                <div className="mt-4 text-sm text-white">
                  <div className="flex items-center justify-center gap-1">
                    <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Special pricing with coupon {couponInfo.code}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!planData && (
        <div className="text-center text-gray-400 mt-8">
          <svg className="h-16 w-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
          </svg>
          <p className="text-lg">Enter a valid coupon code to unlock special plans</p>
          <p className="text-sm mt-1">Coupons provide access to exclusive offers and discounts</p>
        </div>
      )}
    </div>
  );
};

export default SpecialPlans;
